import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Mail, Lock, UserPlus } from 'lucide-react';

type TabType = 'login' | 'signup' | 'magic-link';

export function AuthPage() {
  const { signIn, signUp, signInWithMagicLink } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const { error } = await signIn(email, password);
    if (error) {
      setError('Incorrect email or password. Please try again.');
    }
    setLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!fullName.trim()) {
      setError('Please enter your full name.');
      setLoading(false);
      return;
    }

    const { error } = await signUp(email, password, fullName);
    if (error) {
      setError(error);
    } else {
      setSuccess('Account created! Please sign in with your new credentials.');
      setEmail('');
      setPassword('');
      setFullName('');
      setTimeout(() => setActiveTab('login'), 2000);
    }
    setLoading(false);
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const { error } = await signInWithMagicLink(email);
    if (error) {
      setError(error);
    } else {
      setSuccess('Check your email for a magic link to sign in!');
      setEmail('');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-warm-50 dark:bg-gray-950 flex flex-col items-center justify-center px-6">
      {/* Logo / Branding */}
      <div className="text-center mb-10">
        <div className="text-5xl mb-3">🍽️</div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
          Sabbath Lunch
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
          Welcome — glad you're here.
        </p>
      </div>

      {/* Form Container */}
      <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-3xl shadow-sm border border-warm-100 dark:border-gray-800 p-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 bg-warm-100 dark:bg-gray-800 p-1 rounded-lg">
          <button
            onClick={() => {
              setActiveTab('login');
              setError('');
              setSuccess('');
            }}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              activeTab === 'login'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <Lock className="w-4 h-4 inline mr-1" />
            Login
          </button>
          <button
            onClick={() => {
              setActiveTab('signup');
              setError('');
              setSuccess('');
            }}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              activeTab === 'signup'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-1" />
            Sign Up
          </button>
          <button
            onClick={() => {
              setActiveTab('magic-link');
              setError('');
              setSuccess('');
            }}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
              activeTab === 'magic-link'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-1" />
            Magic Link
          </button>
        </div>

        {/* Login Tab */}
        {activeTab === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 px-3 py-2.5 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <Button type="submit" fullWidth size="lg" loading={loading} className="mt-2">
              Sign In
            </Button>

            <div className="text-center pt-2">
              <a
                href="#forgot"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('magic-link');
                  setEmail('');
                  setError('');
                  setSuccess('');
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Forgot your password?
              </a>
            </div>
          </form>
        )}

        {/* Signup Tab */}
        {activeTab === 'signup' && (
          <form onSubmit={handleSignup} className="space-y-4">
            <Input
              label="Full Name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
              required
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 px-3 py-2.5 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl bg-green-50 dark:bg-green-950 border border-green-100 dark:border-green-900 px-3 py-2.5 text-sm text-green-600 dark:text-green-400">
                {success}
              </div>
            )}

            <Button type="submit" fullWidth size="lg" loading={loading} className="mt-2">
              Create Account
            </Button>
          </form>
        )}

        {/* Magic Link Tab */}
        {activeTab === 'magic-link' && (
          <form onSubmit={handleMagicLink} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                We'll send you a magic link to sign in or create an account.
              </p>
            </div>

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              autoComplete="email"
              required
            />

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 px-3 py-2.5 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-xl bg-green-50 dark:bg-green-950 border border-green-100 dark:border-green-900 px-3 py-2.5 text-sm text-green-600 dark:text-green-400">
                {success}
              </div>
            )}

            <Button type="submit" fullWidth size="lg" loading={loading} className="mt-2">
              Send Magic Link
            </Button>
          </form>
        )}

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6 leading-relaxed">
          This is a private, invite-only app.
          <br />
          Contact your group admin to get access.
        </p>
      </div>
    </div>
  );
}
